/**
 * Conflict Resolution Library
 * Handles sync conflicts between local and cloud data
 */

import type { FavoriteItem } from '@/types/favorites';

export type ConflictStrategy = 'local-wins' | 'cloud-wins' | 'merge' | 'manual';

export interface ConflictResult<T> {
  resolved: T;
  strategy: ConflictStrategy;
  hadConflict: boolean;
  details?: string;
}

export interface FavoriteConflict {
  localItem: FavoriteItem | null;
  cloudItem: FavoriteItem | null;
  conflictType: 'added-both' | 'modified-both' | 'deleted-local' | 'deleted-cloud';
}

export interface SyncDiff {
  toAdd: FavoriteItem[];
  toRemove: { id: string; type: string }[];
  toUpdate: FavoriteItem[];
  conflicts: FavoriteConflict[];
}

export function resolveFavoritesConflict(
  localItems: FavoriteItem[],
  cloudItems: FavoriteItem[],
  strategy: ConflictStrategy = 'merge'
): ConflictResult<FavoriteItem[]> {
  switch (strategy) {
    case 'local-wins':
      return {
        resolved: [...localItems],
        strategy: 'local-wins',
        hadConflict: localItems.length !== cloudItems.length,
        details: 'Local favorites used as source of truth',
      };

    case 'cloud-wins':
      return {
        resolved: [...cloudItems],
        strategy: 'cloud-wins',
        hadConflict: localItems.length !== cloudItems.length,
        details: 'Cloud favorites used as source of truth',
      };

    case 'merge':
      return mergeFavorites(localItems, cloudItems);

    case 'manual':
      return {
        resolved: [],
        strategy: 'manual',
        hadConflict: true,
        details: 'Manual resolution required',
      };

    default:
      return mergeFavorites(localItems, cloudItems);
  }
}

function mergeFavorites(
  localItems: FavoriteItem[],
  cloudItems: FavoriteItem[]
): ConflictResult<FavoriteItem[]> {
  const mergedMap = new Map<string, FavoriteItem>();

  cloudItems.forEach((item) => {
    const key = `${item.type}:${item.id}`;
    mergedMap.set(key, item);
  });

  localItems.forEach((item) => {
    const key = `${item.type}:${item.id}`;
    const existing = mergedMap.get(key);

    if (!existing) {
      mergedMap.set(key, item);
    } else {
      const localTime = new Date(item.addedAt).getTime();
      const cloudTime = new Date(existing.addedAt).getTime();
      if (localTime > cloudTime) {
        mergedMap.set(key, item);
      }
    }
  });

  const merged = Array.from(mergedMap.values()).sort(
    (a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime()
  );

  const localCount = localItems.length;
  const cloudCount = cloudItems.length;
  const mergedCount = merged.length;

  return {
    resolved: merged,
    strategy: 'merge',
    hadConflict: localCount !== cloudCount || mergedCount > Math.max(localCount, cloudCount),
    details: `Merged ${localCount} local and ${cloudCount} cloud items into ${mergedCount} total`,
  };
}

export function calculateSyncDiff(
  localItems: FavoriteItem[],
  cloudItems: FavoriteItem[],
  lastSyncedItems: FavoriteItem[]
): SyncDiff {
  const localMap = new Map(localItems.map((i) => [`${i.type}:${i.id}`, i]));
  const cloudMap = new Map(cloudItems.map((i) => [`${i.type}:${i.id}`, i]));
  const lastSyncMap = new Map(lastSyncedItems.map((i) => [`${i.type}:${i.id}`, i]));

  const toAdd: FavoriteItem[] = [];
  const toRemove: { id: string; type: string }[] = [];
  const toUpdate: FavoriteItem[] = [];
  const conflicts: FavoriteConflict[] = [];

  localItems.forEach((item) => {
    const key = `${item.type}:${item.id}`;
    const cloudItem = cloudMap.get(key);
    const lastSyncItem = lastSyncMap.get(key);

    if (!cloudItem && !lastSyncItem) {
      toAdd.push(item);
    } else if (!cloudItem && lastSyncItem) {
      conflicts.push({
        localItem: item,
        cloudItem: null,
        conflictType: 'deleted-cloud',
      });
    } else if (cloudItem) {
      const localTime = new Date(item.addedAt).getTime();
      const cloudTime = new Date(cloudItem.addedAt).getTime();
      if (localTime !== cloudTime) {
        toUpdate.push(localTime > cloudTime ? item : cloudItem);
      }
    }
  });

  cloudItems.forEach((item) => {
    const key = `${item.type}:${item.id}`;
    const localItem = localMap.get(key);
    const lastSyncItem = lastSyncMap.get(key);

    if (!localItem && !lastSyncItem) {
      toAdd.push(item);
    } else if (!localItem && lastSyncItem) {
      toRemove.push({ id: item.id, type: item.type });
    }
  });

  return { toAdd, toRemove, toUpdate, conflicts };
}

export function resolveVersionConflict(
  localVersion: number,
  cloudVersion: number
): { winner: 'local' | 'cloud'; needsSync: boolean } {
  if (localVersion === cloudVersion) {
    return { winner: 'local', needsSync: false };
  }

  if (localVersion > cloudVersion) {
    return { winner: 'local', needsSync: true };
  }

  return { winner: 'cloud', needsSync: true };
}

export function createConflictKey(item: FavoriteItem): string {
  return `${item.type}:${item.id}`;
}

export function detectDuplicates(items: FavoriteItem[]): FavoriteItem[] {
  const seen = new Set<string>();
  const duplicates: FavoriteItem[] = [];

  items.forEach((item) => {
    const key = createConflictKey(item);
    if (seen.has(key)) {
      duplicates.push(item);
    } else {
      seen.add(key);
    }
  });

  return duplicates;
}

export function deduplicateItems(items: FavoriteItem[]): FavoriteItem[] {
  const map = new Map<string, FavoriteItem>();

  items.forEach((item) => {
    const key = createConflictKey(item);
    const existing = map.get(key);

    if (!existing || new Date(item.addedAt) > new Date(existing.addedAt)) {
      map.set(key, item);
    }
  });

  return Array.from(map.values());
}
